import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'
import { QUERY_KEY } from './getByIdV3'

export type AutoGtOperator = { name: string | null; avatarUrl: string | null }
export type AutoGtCandidate = {
  candidateId: string
  status: 'ready_for_supply' | 'vote_pending' | 'succeeded' | 'exhausted' | 'superseded'
  admittedAt: string
  decision: {
    decidedAt: string
    totalVotes: number
    agreementCount: number
    representativeResolutionId: string
    agreementResolutionIds: string[]
  } | null
  autoGroundTruth: { resolutionId: string; isActive: boolean } | null
  contributorsComplete: boolean
  /** Policy frozen at admission. Optional until every API deployment returns it. */
  minimumTotalVotes?: number
  /** Whole-number percentage, e.g. 85 means 85%. */
  agreementRatio?: number
  maximumTotalVotes?: number
  /** Exhaustion facts, null unless exhausted. Optional until every API deployment returns them. */
  exhaustionReason?: 'max_votes' | 'time_limit' | null
  exhaustedAt?: string | null
  /** Audit Queue task created by Super-Majority exhaustion; null when the Candidate was not escalated. */
  manualAuditFallback?: { auditQueueTaskId: string; createdAt: string | null } | null
}
export type AutoGtContributor = {
  participationId: string
  origin: 'live' | 'super_majority'
  engagementId: string
  taskId: string
  resolutionId: string
  operatorId: string
  acceptedAt: string
  inAgreementGroup: boolean | null
  operator: AutoGtOperator | null
  submittedAt: string | null
  timeToResolveMs: number | null
}
export type AutoGtLineage = {
  engagementId: string
  programId: string | null
  associatedEngagementId: string | null
  dateCreated: string | null
}
export type AutoGtPublicationTarget = {
  engagementId: string
  target: 'source_live' | 'super_majority'
  publicationId: string | null
  status: 'unknown' | 'pending_replication' | 'pending_event' | 'completed'
  groundTruthResolutionId: string | null
  groundTruthCreatedAt: string | null
  completedAt: string | null
}
export type AutoGtPublicationProgress = {
  status: 'unknown' | 'pending' | 'partially_completed' | 'completed'
  expectedTargetsComplete: boolean
  totalTargets: number | null
  visibleTargets: number
  completedTargets: number
  targets: AutoGtPublicationTarget[]
}
export type AutoGtTimelineEvent = { id: string; occurredAt: string } & (
  | { type: 'candidate_admitted'; candidateId: string }
  | { type: 'decision_succeeded'; candidateId: string }
  | { type: 'ground_truth_created'; candidateId: string; engagementId: string; resolutionId: string }
  | { type: 'publication_completed'; candidateId: string; engagementId: string; publicationId: string }
  | {
      type: 'ground_truth_voided'
      engagementId: string
      voidRequestId: string
      voidRequestItemId: string
      actorId: string
      actor: AutoGtOperator | null
    }
)
export type AutoGtTimeline = {
  sourceLiveEngagementId: string
  candidate: AutoGtCandidate | null
  events: AutoGtTimelineEvent[]
  contributors: AutoGtContributor[]
  lineage: AutoGtLineage[]
  publicationProgress: AutoGtPublicationProgress | null
}

function mergeByIdentity<T>(previous: T[], incoming: T[], identity: (item: T) => string): T[] {
  const records = new Map(previous.map((item) => [identity(item), item]))
  for (const item of incoming) records.set(identity(item), item)
  return [...records.values()]
}

export function mergeAutoGtTimeline(
  previous: AutoGtTimeline | undefined,
  incoming: AutoGtTimeline
): AutoGtTimeline {
  const retained = previous?.sourceLiveEngagementId === incoming.sourceLiveEngagementId ? previous : undefined
  const candidate = incoming.candidate ?? retained?.candidate ?? null
  const decision =
    candidate?.candidateId === retained?.candidate?.candidateId ? retained?.candidate?.decision : null
  return {
    ...incoming,
    candidate: candidate ? { ...candidate, decision: candidate.decision ?? decision ?? null } : null,
    events: mergeByIdentity(retained?.events ?? [], incoming.events, (event) => event.id).sort(
      (a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.id.localeCompare(b.id)
    ),
    contributors: mergeByIdentity(
      retained?.contributors ?? [],
      incoming.contributors,
      (vote) => vote.participationId
    ),
    lineage: mergeByIdentity(
      retained?.lineage ?? [],
      incoming.lineage,
      (engagement) => engagement.engagementId
    ),
  }
}

export const getAutoGtTimelineQueryKey = (engagementId?: string) =>
  [QUERY_KEY, engagementId, 'auto-gt-timeline'] as const

export function useGetAutoGtTimeline(engagementId?: string, options?: { enabled?: boolean }) {
  return useQuery<AutoGtTimeline, Error>({
    queryKey: getAutoGtTimelineQueryKey(engagementId),
    queryFn: async () => {
      const { data } = await apiClient.get<AutoGtTimeline>(
        `/int/v3/engagements/${engagementId}/auto-gt-timeline`
      )
      return data
    },
    enabled: Boolean(engagementId) && (options?.enabled ?? true),
    structuralSharing: (previous, incoming) =>
      mergeAutoGtTimeline(previous as AutoGtTimeline | undefined, incoming as AutoGtTimeline),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}
